import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  Tooltip,
  Stack,
  TablePagination,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { apiClient } from "../../services/authService";

interface Subscription {
  _id: string;
  userEmail: string;
  plan: string;
  billingCycle: string;
  price: number;
  status: string;
  paymentMethod: string;
  startDate?: string;
  endDate?: string;
  cancelReason?: string;
  selectedCourseIds?: string[];
  /** Leaflets bought with the companion video add-on. */
  videoCourseIds?: string[];
  /** Unlimited plan only — every companion video included. */
  allVideosIncluded?: boolean;
  metadata?: {
    payerName?: string;
    payerPhone?: string;
    country?: string;
    reference?: string;
    planLabel?: string;
    submittedAt?: string;
    selectedCourseTitles?: string[];
    /** Titles for `videoCourseIds`, captured at checkout. */
    videoCourseTitles?: string[];
    pendingVerification?: boolean;
  };
  createdAt: string;
}

// Plans that unlock every course — the activate dialog has no list to pick from.
const isFullAccessPlan = (plan: string) => plan === "pro" || plan === "premium";

// The video-only add-on: verifying it grants companion videos on leaflets the
// user already owns, and must never hand out leaflet access on its own.
const isVideoAddonPlan = (plan: string) => plan === "video";

type DialogType = "activate" | "reject" | "cancel" | "renew" | "delete" | "details" | null;

const STATUS_COLORS: Record<string, "warning" | "success" | "error" | "default" | "info"> = {
  pending: "warning",
  active: "success",
  cancelled: "error",
  expired: "default",
  trial: "info",
};

const STATUS_TABS = ["all", "pending", "active", "cancelled", "expired"];

// Manual payment methods that require admin verification before granting access
const MANUAL_METHODS = ["wish_money", "omt", "bank", "whatsapp"];

// A subscription needs verification when:
//  • its payment method is a manual (non-gateway) method, AND
//  • it has not yet been explicitly approved by an admin
//  • admin approval is recorded by setting cancelReason = "verified"
const needsVerification = (sub: { paymentMethod: string; status: string; cancelReason?: string }) =>
  MANUAL_METHODS.includes(sub.paymentMethod) &&
  sub.status === "active" &&
  sub.cancelReason !== "verified";

const fmt = (d?: string) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

const AdminSubscriptions: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const [dialogType, setDialogType] = useState<DialogType>(null);
  const [selectedSub, setSelectedSub] = useState<Subscription | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  // Course-grant state (used only in the activate dialog)
  const [availableCourses, setAvailableCourses] = useState<{ _id: string; title: string }[]>([]);
  const [grantedCourseIds, setGrantedCourseIds] = useState<string[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);

  /* -- fetch -- */
  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (statusFilter === "pending") {
        // No subscription is ever literally saved with status:"pending" — manual
        // payments (Wish Money/OMT/Bank/WhatsApp) are created as status:"active" and
        // gated by needsVerification() until an admin approves them. Fetch the active
        // set and filter/paginate client-side so the "Pending" tab actually matches
        // the same rows the "pending verification" chip highlights elsewhere.
        const params: Record<string, any> = { status: "active", limit: 500 };
        if (search.trim()) params.search = search.trim();

        const res = await apiClient.get<{ data: Subscription[] }>("/subscriptions", { params });
        const awaitingVerification = (res.data.data ?? []).filter(needsVerification);

        setTotal(awaitingVerification.length);
        setSubscriptions(awaitingVerification.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage));
      } else {
        const params: Record<string, any> = { page: page + 1, limit: rowsPerPage };
        if (statusFilter !== "all") params.status = statusFilter;
        if (search.trim()) params.search = search.trim();

        const res = await apiClient.get<{
          data: Subscription[];
          pagination: { total: number };
        }>("/subscriptions", { params });

        setSubscriptions(res.data.data ?? []);
        setTotal(res.data.pagination?.total ?? (res.data.data ?? []).length);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to load subscriptions.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, page, rowsPerPage]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  // Load courses when the activate dialog opens (for basic/starter plan only)
  useEffect(() => {
    if (dialogType !== "activate" || !selectedSub) return;
    setGrantedCourseIds(selectedSub.selectedCourseIds ?? []);
    // Full-access plans don't need a course list, and a video add-on has no
    // leaflets to grant.
    if (isFullAccessPlan(selectedSub.plan) || isVideoAddonPlan(selectedSub.plan)) return;
    setCoursesLoading(true);
    setAvailableCourses([]);
    apiClient.get("/courses", { params: { limit: 200 } })
      .then(res => {
        const list: any[] = res.data?.courses ?? (Array.isArray(res.data) ? res.data : []);
        setAvailableCourses(list.filter(c => c._id && c.title));
      })
      .catch(() => setAvailableCourses([]))
      .finally(() => setCoursesLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogType]);

  /* -- helpers -- */
  const openDialog = (type: DialogType, sub: Subscription) => {
    setSelectedSub(sub);
    setDialogType(type);
    setRejectReason("");
  };
  const closeDialog = () => {
    setDialogType(null);
    setSelectedSub(null);
    setRejectReason("");
    setGrantedCourseIds([]);
  };
  const flash = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 4000);
  };

  /* -- actions -- */
  const runAction = async (action: () => Promise<void>, id: string) => {
    setActionLoading(id);
    closeDialog();
    try {
      await action();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Action failed.");
    } finally {
      setActionLoading(null);
    }
  };


  const handleActivate = () => {
    const courseIds = [...grantedCourseIds];
    const sub = selectedSub!;
    runAction(async () => {
      // cancelReason:"verified" is the admin-approval signal. Classes.tsx reads
      // this field (alongside paymentMethod) to decide whether to grant access.
      // A video add-on's course list stays untouched — the order carries only
      // the video ids, and sending leaflets here would unlock them for $3.
      const grant = isVideoAddonPlan(sub.plan) ? {} : { selectedCourseIds: courseIds };
      await apiClient.put(`/subscriptions/${sub._id}`, {
        status: "active",
        ...grant,
        cancelReason: "verified",
        metadata: sub.metadata
          ? { ...sub.metadata, pendingVerification: false }
          : { pendingVerification: false },
      });
      setSubscriptions((prev) =>
        prev.map((s) =>
          s._id === sub._id
            ? { ...s, status: "active", ...grant, cancelReason: "verified" }
            : s
        )
      );
      flash("Subscription activated — access granted.");
    }, sub._id);
  };

  const handleReject = () => {
    const sub = selectedSub!;
    runAction(async () => {
      await apiClient.put(`/subscriptions/${sub._id}`, {
        status: "cancelled",
        cancelReason: "Payment not verified by admin",
      });
      setSubscriptions((prev) =>
        prev.map((s) =>
          s._id === sub._id
            ? { ...s, status: "cancelled", cancelReason: "Payment not verified by admin" }
            : s
        )
      );
      flash("Subscription request rejected.");
    }, sub._id);
  };

  const handleCancel = () => {
    const sub = selectedSub!;
    runAction(async () => {
      await apiClient.put(`/${"subscriptions"}/${sub._id}`, { status: "cancelled" });
      setSubscriptions((prev) =>
        prev.map((s) => (s._id === sub._id ? { ...s, status: "cancelled" } : s))
      );
      flash("Subscription cancelled.");
    }, sub._id);
  };

  const handleRenew = () => {
    const sub = selectedSub!;
    runAction(async () => {
      // Try renew endpoint; fall back to PUT status:active for user-subscriptions
      try {
        await apiClient.post(`/subscriptions/${sub._id}/renew`);
      } catch {
        await apiClient.put(`/${"subscriptions"}/${sub._id}`, { status: "active" });
      }
      setSubscriptions((prev) =>
        prev.map((s) => (s._id === sub._id ? { ...s, status: "active" } : s))
      );
      flash("Subscription renewed successfully.");
    }, sub._id);
  };

  const handleDelete = () => {
    const sub = selectedSub!;
    runAction(async () => {
      await apiClient.delete(`/${"subscriptions"}/${sub._id}`);
      setSubscriptions((prev) => prev.filter((s) => s._id !== sub._id));
      setTotal((t) => t - 1);
      flash("Subscription deleted permanently.");
    }, sub._id);
  };

  const pendingCount = statusFilter === "pending" ? total : subscriptions.filter(needsVerification).length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight={700}>
          Subscription Requests
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={fetchSubscriptions} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* Alerts */}
      {pendingCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {pendingCount} pending payment request{pendingCount > 1 ? "s" : ""} awaiting
          manual review.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search by email..."
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2, minWidth: 260 }}
      />

      {/* Status tabs */}
      <Tabs
        value={statusFilter}
        onChange={(_, v) => {
          setStatusFilter(v);
          setPage(0);
        }}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        textColor="primary"
        indicatorColor="primary"
      >
        {STATUS_TABS.map((tab) => (
          <Tab
            key={tab}
            value={tab}
            label={tab === "all" ? "All" : tab.charAt(0).toUpperCase() + tab.slice(1)}
          />
        ))}
      </Tabs>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#fafafa" }}>
                  <TableCell><strong>User</strong></TableCell>
                  <TableCell><strong>Plan</strong></TableCell>
                  <TableCell><strong>Price</strong></TableCell>
                  <TableCell><strong>Method</strong></TableCell>
                  <TableCell><strong>Phone</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Submitted</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No subscriptions found.
                    </TableCell>
                  </TableRow>
                ) : (
                  subscriptions.map((sub) => (
                    <TableRow key={sub._id} hover>
                      <TableCell
                        sx={{
                          maxWidth: 180,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {sub.userEmail}
                      </TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>
                        {sub.metadata?.planLabel || sub.plan}
                      </TableCell>
                      <TableCell>${sub.price}</TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>
                        {(sub.paymentMethod || "-").replace(/_/g, " ")}
                      </TableCell>
                      <TableCell>{sub.metadata?.payerPhone || "-"}</TableCell>
                      <TableCell>
                        {needsVerification(sub) ? (
                          <Chip label="pending verification" color="warning" size="small"
                            sx={{ textTransform: "capitalize" }} />
                        ) : (
                          <Chip label={sub.status} color={STATUS_COLORS[sub.status] ?? "default"}
                            size="small" sx={{ textTransform: "capitalize" }} />
                        )}
                      </TableCell>
                      <TableCell>
                        {fmt(sub.metadata?.submittedAt || sub.createdAt)}
                      </TableCell>

                      {/* Action icons */}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {/* Details - always visible */}
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => openDialog("details", sub)}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* Activate - pending status OR unverified manual payment */}
                          {(sub.status === "pending" || needsVerification(sub)) && (
                            <Tooltip title="Activate">
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={actionLoading === sub._id}
                                  onClick={() => openDialog("activate", sub)}
                                >
                                  {actionLoading === sub._id ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    <CheckCircleOutlineIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {/* Reject - pending status OR unverified manual payment */}
                          {(sub.status === "pending" || needsVerification(sub)) && (
                            <Tooltip title="Reject Request">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={actionLoading === sub._id}
                                  onClick={() => openDialog("reject", sub)}
                                >
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {/* Cancel - active/trial and already verified (or not a manual payment) */}
                          {(sub.status === "active" || sub.status === "trial")
                            && !needsVerification(sub) && (
                            <Tooltip title="Cancel Subscription">
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  disabled={actionLoading === sub._id}
                                  onClick={() => openDialog("cancel", sub)}
                                >
                                  <CancelOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {/* Renew - expired or cancelled */}
                          {(sub.status === "expired" || sub.status === "cancelled") && (
                            <Tooltip title="Renew Subscription">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={actionLoading === sub._id}
                                  onClick={() => openDialog("renew", sub)}
                                >
                                  <AutorenewIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {/* Delete - always available */}
                          <Tooltip title="Delete Permanently">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={actionLoading === sub._id}
                                onClick={() => openDialog("delete", sub)}
                                sx={{ opacity: 0.55 }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Paper>
      )}

      {/* Activate Dialog */}
      <Dialog open={dialogType === "activate"} onClose={closeDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Activate Subscription</DialogTitle>
        <DialogContent dividers>
          {selectedSub && (() => {
            const isFullAccess = isFullAccessPlan(selectedSub.plan);
            const isVideoAddon = isVideoAddonPlan(selectedSub.plan);
            const videoTitles = selectedSub.metadata?.videoCourseTitles ?? [];
            return (
              <Stack spacing={2.5}>
                {/* Payment verification summary */}
                <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#F0F7FF", border: "1px solid #BBDEFB" }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#1565C0", mb: 1.5 }}>
                    Payment Verification
                  </Typography>
                  <Stack spacing={0.8}>
                    {[
                      ["User", selectedSub.userEmail],
                      ["Plan", selectedSub.metadata?.planLabel || selectedSub.plan],
                      ["Price", `$${selectedSub.price}`],
                      ["Method", (selectedSub.paymentMethod || "-").replace("_", " ")],
                      ["Reference", selectedSub.metadata?.reference || "-"],
                      ["Phone", selectedSub.metadata?.payerPhone || "-"],
                      ["Payer Name", selectedSub.metadata?.payerName || "-"],
                    ].map(([label, value]) => (
                      <Stack key={label} direction="row" spacing={1}>
                        <Typography variant="body2" sx={{ color: "#6B7280", minWidth: 90, flexShrink: 0 }}>
                          {label}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ color: "#111827", wordBreak: "break-all" }}>
                          {value}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>

                <Divider />

                {/* Course access section */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
                    {isVideoAddon ? "Grant Companion Video" : "Grant Course Access"}
                  </Typography>

                  {isVideoAddon ? (
                    <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#F0F7FF", border: "1px solid #BBDEFB" }}>
                      <Typography fontWeight={700} sx={{ color: "#1565C0" }}>
                        ▶ Companion video add-on
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5, mb: videoTitles.length ? 1 : 0 }}>
                        Activating unlocks the companion video on the leaflets below, which the user
                        already owns. No new course access is granted.
                      </Typography>
                      {videoTitles.map((title, i) => (
                        <Typography key={i} variant="body2" fontWeight={600} sx={{ color: "#111827" }}>
                          • {title}
                        </Typography>
                      ))}
                    </Box>
                  ) : isFullAccess ? (
                    <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#ECFDF5", border: "1px solid #A7F3D0" }}>
                      <Typography fontWeight={700} sx={{ color: "#059669" }}>
                        ✓ Full access to all courses
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5 }}>
                        This plan grants unlimited access to every course on the platform.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ color: "#6B7280", mb: 1.5 }}>
                        Check the courses to unlock for this user. Pre-selected courses are what the user requested.
                        <Box component="span" sx={{ fontWeight: 700, color: "#1565C0" }}>
                          {" "}({grantedCourseIds.length} selected)
                        </Box>
                      </Typography>

                      {coursesLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : (
                        <Box sx={{
                          maxHeight: 280, overflowY: "auto",
                          border: "1px solid #E5E7EB", borderRadius: "10px",
                        }}>
                          {availableCourses.length === 0 ? (
                            <Typography variant="body2" sx={{ color: "#9CA3AF", p: 2, textAlign: "center" }}>
                              No courses found.
                            </Typography>
                          ) : (
                            <FormGroup>
                              {availableCourses.map(course => (
                                <FormControlLabel
                                  key={course._id}
                                  control={
                                    <Checkbox
                                      checked={grantedCourseIds.includes(course._id)}
                                      onChange={e =>
                                        setGrantedCourseIds(prev =>
                                          e.target.checked
                                            ? [...prev, course._id]
                                            : prev.filter(id => id !== course._id)
                                        )
                                      }
                                      size="small"
                                      sx={{ color: "#9CA3AF", "&.Mui-checked": { color: "#1565C0" } }}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2" sx={{ fontWeight: grantedCourseIds.includes(course._id) ? 700 : 400 }}>
                                      {course.title}
                                    </Typography>
                                  }
                                  sx={{
                                    mx: 0, px: 1.5, py: 0.6,
                                    borderBottom: "1px solid #F3F4F6",
                                    "&:last-child": { borderBottom: "none" },
                                    "&:hover": { bgcolor: "rgba(21,101,192,0.04)" },
                                  }}
                                />
                              ))}
                            </FormGroup>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2 }}>
          <Button onClick={closeDialog} sx={{ textTransform: "none", color: "#6B7280" }}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleActivate}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px", px: 3 }}>
            {selectedSub && isVideoAddonPlan(selectedSub.plan) ? "✓ Activate & Grant Video" : "✓ Activate & Grant Access"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={dialogType === "reject"} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Subscription Request</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Reject the pending request from <strong>{selectedSub?.userEmail}</strong>?
            The subscription will be marked as cancelled.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Payment not confirmed"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Back</Button>
          <Button variant="contained" color="error" onClick={handleReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={dialogType === "cancel"} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cancel the active subscription for <strong>{selectedSub?.userEmail}</strong>?
            The user will lose access immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Back</Button>
          <Button variant="contained" color="warning" onClick={handleCancel}>
            Cancel Subscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* Renew Dialog */}
      <Dialog open={dialogType === "renew"} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Renew Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Renew the subscription for <strong>{selectedSub?.userEmail}</strong> and
            restore active access?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleRenew}>
            Renew
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={dialogType === "delete"} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: "error.main" }}>Delete Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete the record for <strong>{selectedSub?.userEmail}</strong>?
            This action <strong>cannot be undone</strong>.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={dialogType === "details"} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Subscription Details</DialogTitle>
        <DialogContent dividers>
          {selectedSub && (
            <Stack spacing={1.5}>
              {(
                [
                  ["User Email", selectedSub.userEmail],
                  ["Plan", selectedSub.metadata?.planLabel || selectedSub.plan],
                  ["Billing Cycle", selectedSub.billingCycle],
                  ["Price", `$${selectedSub.price}`],
                  ["Status", selectedSub.status],
                  ["Payment Method", (selectedSub.paymentMethod || "-").replace("_", " ")],
                  ["Payer Name", selectedSub.metadata?.payerName || "-"],
                  ["Payer Phone", selectedSub.metadata?.payerPhone || "-"],
                  ["Country", selectedSub.metadata?.country || "-"],
                  ["Reference", selectedSub.metadata?.reference || "-"],
                  ["Submitted At", fmt(selectedSub.metadata?.submittedAt)],
                  ["Created At", fmt(selectedSub.createdAt)],
                  ...(selectedSub.startDate
                    ? [["Start Date", fmt(selectedSub.startDate)] as [string, string]]
                    : []),
                  ...(selectedSub.endDate
                    ? [["End Date", fmt(selectedSub.endDate)] as [string, string]]
                    : []),
                  // "verified" is an internal marker — don't surface it to admin
                  ...(selectedSub.cancelReason && selectedSub.cancelReason !== "verified"
                    ? [["Cancel Reason", selectedSub.cancelReason] as [string, string]]
                    : []),
                ] as [string, string][]
              ).map(([label, value]) => (
                <Stack key={label} direction="row" spacing={2}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}
                    sx={{ textTransform: "capitalize", wordBreak: "break-all" }}>
                    {value}
                  </Typography>
                </Stack>
              ))}

              {/* Selected courses */}
              {(selectedSub.metadata?.selectedCourseTitles ?? []).length > 0 && (
                <>
                  <Divider />
                  <Stack direction="row" spacing={2}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                      Selected Courses
                    </Typography>
                    <Stack spacing={0.3}>
                      {selectedSub.metadata!.selectedCourseTitles!.map((title, i) => (
                        <Typography key={i} variant="body2" fontWeight={500}>
                          • {title}
                        </Typography>
                      ))}
                    </Stack>
                  </Stack>
                </>
              )}

              {/* Companion videos — bought with a plan or as the video-only add-on */}
              {((selectedSub.metadata?.videoCourseTitles ?? []).length > 0 || selectedSub.allVideosIncluded) && (
                <>
                  <Divider />
                  <Stack direction="row" spacing={2}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                      Companion Videos
                    </Typography>
                    <Stack spacing={0.3}>
                      {selectedSub.allVideosIncluded ? (
                        <Typography variant="body2" fontWeight={500}>All videos included</Typography>
                      ) : (
                        selectedSub.metadata!.videoCourseTitles!.map((title, i) => (
                          <Typography key={i} variant="body2" fontWeight={500}>
                            • {title}
                          </Typography>
                        ))
                      )}
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSubscriptions;
